// src/hooks/useSubscription.js
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

export function useSubscription(user) {
  const [subscription, setSubscription] = useState({
    tier: 'free',
    status: 'active',
    loading: true,
    isActive: false,
    isPremium: false
  });

  const fetchSubscription = useCallback(async () => {
    if (!user?.id) {
      setSubscription({
        tier: 'free',
        status: 'inactive',
        loading: false,
        isActive: false,
        isPremium: false
      });
      return;
    }

    try {
      // Fetch user profile with subscription info
      const { data: profile, error } = await supabase
        .from('user_profiles')
        .select('subscription_tier, subscription_status, subscription_end_date')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching subscription:', error);
        throw error;
      }

      const tier = profile?.subscription_tier || 'free';
      const status = profile?.subscription_status || 'active';
      const endDate = profile?.subscription_end_date;
      
      // Check if subscription is actually active
      const isActive = tier !== 'free' && status === 'active' && 
                      (!endDate || new Date(endDate) > new Date());

      setSubscription({
        tier,
        status,
        endDate,
        loading: false,
        isActive,
        isPremium: isActive && (tier === 'premium_monthly' || tier === 'premium_annual')
      });
    } catch (err) {
      console.error('Error loading subscription:', err);
      setSubscription({
        tier: 'free',
        status: 'inactive',
        loading: false,
        isActive: false,
        isPremium: false
      });
    }
  }, [user?.id]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  return { ...subscription, refetch: fetchSubscription };
}

// Hook to check AI analysis usage limits
export function useAIAnalysisLimit(user) {
  const [usage, setUsage] = useState({
    count: 0,
    limit: 2,
    canUse: true,
    loading: true
  });
  
  const { isPremium, loading: subscriptionLoading } = useSubscription(user);

  const checkUsage = useCallback(async () => {
    if (!user?.id) {
      setUsage({ count: 0, limit: 2, canUse: false, loading: false });
      return;
    }

    // Wait for subscription to load
    if (subscriptionLoading) {
      return;
    }

    // Premium users have unlimited
    if (isPremium) {
      setUsage({ count: 0, limit: Infinity, canUse: true, loading: false });
      return;
    }

    try {
      
      
      // Get this month's usage
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const { data, error } = await supabase
        .from('ai_analysis_usage')
        .select('count')
        .eq('user_id', user.id)
        .gte('analysis_date', startOfMonth.toISOString().split('T')[0]);

      if (error) throw error;

      const totalCount = data?.reduce((sum, row) => sum + (row.count || 0), 0) || 0;
      const limit = 2; // Free tier limit
      
      setUsage({
        count: totalCount,
        limit,
        canUse: totalCount < limit,
        loading: false
      });
    } catch (err) {
      console.error('Error checking AI usage:', err);
      setUsage({ count: 0, limit: 2, canUse: true, loading: false });
    }
  }, [user?.id, isPremium, subscriptionLoading]);

  const incrementUsage = useCallback(async () => {
    if (!user?.id || isPremium) return true;

    try {
      const today = new Date().toISOString().split('T')[0];
      
      // Insert or increment usage for today
      const { error } = await supabase.rpc('increment_ai_usage', {
        p_user_id: user.id,
        p_date: today
      });

      if (error) {
        // If function doesn't exist, use upsert
        const { error: upsertError } = await supabase
          .from('ai_analysis_usage')
          .upsert({
            user_id: user.id,
            analysis_date: today,
            count: 1
          }, {
            onConflict: 'user_id,analysis_date',
            ignoreDuplicates: false
          });
        
        if (upsertError) throw upsertError;
      }

      await checkUsage();
      return true;
    } catch (err) {
      console.error('Error incrementing usage:', err);
      return false;
    }
  }, [user?.id, isPremium, checkUsage]);

  useEffect(() => {
    checkUsage();
  }, [checkUsage]);

  return { ...usage, incrementUsage, refetch: checkUsage };
}

// Hook to check workout history limits
export function useWorkoutHistoryLimit(user) {
  const { isPremium } = useSubscription(user);
  
  const getHistoryLimit = useCallback(() => {
    if (isPremium) {
      return null; // No limit
    }
    
    // Free tier: 30 days
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return thirtyDaysAgo;
  }, [isPremium]);

  return {
    isPremium,
    limitDate: getHistoryLimit(),
    hasLimit: !isPremium
  };
}